/**
 * Builds the "effective" user seen by the app while a super admin impersonates
 * a client account (AMLIQ-137).
 *
 * It keeps the super admin's own identity — `id`, `email`, `fullName`,
 * `avatarUrl` — so the sidebar footer still shows the super admin and every
 * action stays attributed to them (AC4). It overlays the impersonated account's
 * context — the impersonation is granted the union of the account's **Admin +
 * Compliance Officer** roles (per swagger/admin-auth.json), so both `hasRole`
 * checks pass; `role` stays `ADMIN_USER` (the highest, for landing/level) — plus
 * the tenant id and sector, so the whole app behaves as that account's admin +
 * AMLCo: nav, gates, sector-driven forms, and account-scoped data resolve from
 * `user`.
 */
import type { AuthUser, ImpersonationTenant, TenantDetail } from '../types'
import { UserRole } from '@/lib/rbac'

export function buildImpersonatedUser(
  realUser: AuthUser,
  tenant: ImpersonationTenant,
  tenantDetail: TenantDetail | null,
): AuthUser {
  return {
    ...realUser,
    // Act as the account's Admin + Compliance Officer (the impersonation grant).
    // `role` is the highest (Admin) for landing/level; `roles` carries both so
    // role-gated features (e.g. CO-only risk-rating first-set) resolve correctly.
    role: UserRole.AdminUser,
    roles: [UserRole.AdminUser, UserRole.ComplianceOfficer],
    roleNames: ['Admin', 'Compliance Officer'],
    // Drop any super-admin-only explicit grants; role defaults apply.
    permissions: undefined,
    // Scope to the impersonated tenant + its sector (drives sector-specific UI).
    accountId: tenant.id,
    accountSector: tenantDetail?.sector?.name ?? null,
  }
}
